"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Shared state between the <HighlightSlider /> and <ContextualDock />.
 *
 * The slider owns the video players and scroll position; it registers its
 * control callbacks here so the dock can render transport controls without
 * knowing about the players.
 */
const SliderPlayerContext = createContext(null);

const NOOP = () => {};

export function SliderPlayerProvider({ children }) {
  const [slides, setSlides] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const controlsRef = useRef({ togglePlay: NOOP, toggleMute: NOOP, goTo: NOOP });

  const registerControls = useCallback((controls) => {
    controlsRef.current = { togglePlay: NOOP, toggleMute: NOOP, goTo: NOOP, ...controls };
    setMounted(true);
    return () => {
      controlsRef.current = { togglePlay: NOOP, toggleMute: NOOP, goTo: NOOP };
      setMounted(false);
    };
  }, []);

  const togglePlay = useCallback(() => controlsRef.current.togglePlay(), []);
  const toggleMute = useCallback(() => controlsRef.current.toggleMute(), []);
  const goTo = useCallback((i) => controlsRef.current.goTo(i), []);

  const value = useMemo(
    () => ({
      slides,
      setSlides,
      activeIndex,
      setActiveIndex,
      playing,
      setPlaying,
      muted,
      setMuted,
      progress,
      setProgress,
      mounted,
      registerControls,
      togglePlay,
      toggleMute,
      goTo,
    }),
    [slides, activeIndex, playing, muted, progress, mounted, registerControls, togglePlay, toggleMute, goTo]
  );

  return (
    <SliderPlayerContext.Provider value={value}>
      {children}
    </SliderPlayerContext.Provider>
  );
}

export function useSliderPlayer() {
  return useContext(SliderPlayerContext);
}
